import {
    Navbar,
    NavbarBrand,
    NavbarContent,
    type NavbarProps
} from "@heroui/react";
import clsx from "clsx";
import { useMemo } from "react";
import type { LayoutSettings } from "../types";

export interface VGroveNavbarProps extends Omit<NavbarProps, "children"> {
    settings: LayoutSettings;

    // 第一个区域：NavbarBrand 配置
    brandContent?: React.ReactNode;

    // 第二个区域：中间内容区域（完全自定义）
    centerContent?: React.ReactNode | ((props: { className?: string }) => React.ReactNode);

    // 第三个区域：右侧自定义区域
    endContent?: React.ReactNode | ((props: { className?: string }) => React.ReactNode);
}

export const VGroveNavbar: React.FC<VGroveNavbarProps> = ({
    settings,
    className,
    brandContent,
    centerContent,
    endContent,
    ...navbarProps
}) => {
    const navbarConfig = settings.navbar;
    const isSticky = navbarConfig?.sticky ?? false;
    const isTransparent = navbarConfig?.transparent ?? false;
    const height = navbarConfig?.height || '64px';

    // 使用 useMemo 缓存渲染内容，避免重复计算
    const brandContentMemo = useMemo(() => brandContent || null, [brandContent]);

    const centerContentMemo = useMemo(() => {
        if (!centerContent) return null;

        if (typeof centerContent === 'function') {
            return centerContent({ className: "flex items-center gap-4" });
        }

        return centerContent;
    }, [centerContent]);

    const endContentMemo = useMemo(() => {
        if (!endContent) return null;

        if (typeof endContent === 'function') {
            return endContent({ className: "flex items-center gap-2" });
        }

        return endContent;
    }, [endContent]);

    return (
        <Navbar
            height={height}
            className={clsx(
                {
                    'border-default-100': !isTransparent,
                    'bg-transparent': isTransparent,
                    'relative': !isSticky,
                    'border-b-small': !isTransparent,
                },
                className
            )}
            isBlurred
            maxWidth="full"
            {...navbarProps}
        >
            {/* 第一个区域：NavbarBrand */}
            {brandContentMemo && (
                <NavbarBrand>
                    {brandContentMemo}
                </NavbarBrand>
            )}

            {/* 第二个区域：中间内容 */}
            {centerContentMemo && (
                <div className="self-end flex items-center h-full min-w-0 max-w-full flex-shrink-1 overflow-hidden">
                    {centerContentMemo}
                </div>
            )}

            {/* 第三个区域：右侧自定义区域 */}
            {endContentMemo && (
                <NavbarContent className="flex-1" justify="end">
                    {endContentMemo}
                </NavbarContent>
            )}
        </Navbar>
    );
};